'use client';

import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import useLocale from 'hooks/useLocale';

/**
 * A wrapper around React Router Link that automatically preserves the locale query parameter
 * Usage: <LocaleLink to="/dashboard">Dashboard</LocaleLink> or <LocaleLink href="/dashboard">Dashboard</LocaleLink>
 */
export default function LocaleLink({ href, to, ...props }) {
  const locale = useLocale();
  const targetHref = to || href;

  const hrefWithLocale = useMemo(() => {
    if (!targetHref) return targetHref;

    // Handle string href
    if (typeof targetHref === 'string') {
      // Don't modify external URLs
      if (targetHref.startsWith('http://') || targetHref.startsWith('https://')) {
        return targetHref;
      }

      // Don't modify anchor-only links
      if (targetHref === '#' || targetHref.startsWith('#')) {
        return targetHref;
      }

      // Parse the URL
      const [path, search] = targetHref.split('?');
      const params = new URLSearchParams(search || '');

      // Add or update locale parameter
      params.set('locale', locale);

      return `${path}?${params.toString()}`;
    }

    // Handle object href (with pathname, query, etc.)
    if (typeof targetHref === 'object') {
      return {
        ...targetHref,
        search: {
          ...targetHref.search,
          locale,
        },
      };
    }

    return targetHref;
  }, [targetHref, locale]);

  return <Link to={hrefWithLocale} {...props} />;
}
